import { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Download, BadgeCheck, Package, Flame } from 'lucide-react';
import { useLang } from '../i18n';
import { EASE } from './motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const catLabel = (cat, t) => t(`catalogo.${cat}`);

export const priceFmt = (p) => `€ ${p.toFixed(2).replace('.', ',')}`;

const ProductModal = ({ product, onClose }) => {
  const { lang, t } = useLang();

  useEffect(() => {
    document.body.style.overflow = product ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [product]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <AnimatePresence>
      {product && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-end md:items-center justify-center p-0 md:p-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          data-testid="product-modal"
        >
          <div className="absolute inset-0 bg-[#22303C]/80 backdrop-blur-sm" onClick={onClose} data-testid="product-modal-backdrop" />
          <motion.div
            className="relative bg-background w-full max-w-4xl max-h-[92vh] overflow-y-auto border border-foreground/15"
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.55, ease: EASE }}
          >
            <button
              onClick={onClose}
              data-testid="product-modal-close-button"
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-background border border-foreground/15 flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors duration-300"
              aria-label={t('nav.close')}
            >
              <X className="w-5 h-5" />
            </button>
            <div className="grid md:grid-cols-2">
              <div className="overflow-hidden aspect-square bg-muted">
                <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-8 md:p-10 flex flex-col">
                <span className="font-display text-xs uppercase tracking-[0.2em] font-bold text-secondary mb-3" data-testid="product-modal-category">
                  {catLabel(product.category, t)}
                </span>
                <h3 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight leading-none mb-5" data-testid="product-modal-name">
                  {product.name}
                </h3>
                <p className="text-foreground/70 leading-relaxed mb-8" data-testid="product-modal-description">
                  {product.description[lang]}
                </p>
                <div className="space-y-4 border-t border-foreground/10 pt-6 mb-8">
                  <div className="flex items-center gap-3 text-sm">
                    <Package className="w-4 h-4 text-secondary shrink-0" />
                    <span className="text-foreground/60">{t('catalogo.format')}:</span>
                    <span className="font-medium" data-testid="product-modal-format">{product.format[lang]}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Flame className="w-4 h-4 text-secondary shrink-0" />
                    <span className="text-foreground/60">{t('catalogo.prepLabel')}:</span>
                    <span className="font-medium" data-testid="product-modal-prep">{t(`catalogo.prep.${product.prep}`)}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <BadgeCheck className="w-4 h-4 text-secondary shrink-0" />
                    <span className="font-medium">{t('catalogo.certified')}</span>
                  </div>
                  <div className="flex items-baseline gap-3 pt-2">
                    <span className="text-foreground/60 text-sm">{t('catalogo.price')}</span>
                    <span className="font-display font-extrabold text-3xl" data-testid="product-modal-price">{priceFmt(product.price)}</span>
                  </div>
                </div>
                <div className="mt-auto flex flex-col sm:flex-row gap-3">
                  <a
                    href={`${API}/products/${product.slug}/sheet.pdf?lang=${lang}`}
                    data-testid="product-modal-download-button"
                    className="flex-1 flex items-center justify-center gap-2 bg-primary text-primary-foreground px-6 py-3.5 font-display font-bold text-sm uppercase tracking-wider hover:bg-secondary transition-colors duration-300"
                  >
                    <Download className="w-4 h-4" />
                    {t('catalogo.sheet')}
                  </a>
                </div>
                <p className="text-xs text-foreground/40 mt-5">{t('catalogo.b2bNote')}</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProductModal;
